import { useMemo } from "react"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getImagePreviewSrc } from "components/Shared/fileHandlers"
import { usePortalTenant } from "context/PortalTenantContext"
import { getTenantBrandingFromStorage } from "utils/tenantBranding"
import { buildItemPreviewProps } from "./buildItemPreviewProps"
import { buildItemPreviewThemeStyle } from "./itemPreviewTheme"
import { useItemPreviewTheme } from "./useItemPreviewTheme"
import { useFastMovingIconUrl } from "./useFastMovingIconUrl"

const pickLogoUrl = (...candidates) => {
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim()
    }
  }
  return ""
}

/** Shared live form → preview card data + theme for a single preview field. */
export const useItemPreviewCardData = ({
  values,
  selectedTags = [],
  mode = "add",
  enabled = true,
}) => {
  const { i18n } = useTranslation()
  const { theme } = useItemPreviewTheme(enabled)
  const { iconUrl, settingsEnabled } = useFastMovingIconUrl(enabled)
  const authLogoUrl = useSelector(
    state => state.Login?.authUser?.tenantBranding?.logoUrl || ""
  )
  const { tenantBranding: portalTenantBranding } = usePortalTenant()
  const themeStyle = useMemo(() => buildItemPreviewThemeStyle(theme), [theme])
  const fallbackImage = useMemo(
    () =>
      pickLogoUrl(
        authLogoUrl,
        portalTenantBranding?.logoUrl,
        getTenantBrandingFromStorage()?.logoUrl
      ),
    [authLogoUrl, portalTenantBranding?.logoUrl]
  )

  const item = useMemo(
    () =>
      buildItemPreviewProps({
        values,
        selectedTags,
        mode,
        language: i18n.language,
        getImagePreviewSrc,
      }),
    [values, selectedTags, mode, i18n.language]
  )

  return {
    item,
    themeStyle,
    fastMovingIconUrl: iconUrl,
    fastMovingSettingsEnabled: settingsEnabled,
    fallbackImage,
  }
}
